import React, { useEffect, useState } from 'react';
import Framework from '@greenville/framework';
import { Autocomplete } from '@mui/material';
import { Grid, ListItem, TextField } from '@material-ui/core';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import Paper from '@material-ui/core/Paper';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import axios from 'axios';
import _ from 'lodash';
import { useHistory } from 'react-router-dom';
import * as constants from '../../../../common/constants';
import env from '../../../../common/env';
import utils from '../../../../common/utils';
import useTestBedConfigureStyles from './TestbedConfigureStyles';

const { version } = require('@vega/aitutor/package.json');

const GuidedStudyTestbedConfigure = () => {
  const history = useHistory();
  const classes = useTestBedConfigureStyles();
  const [tenant, setTenant] = useState('');
  const [tenants, setTenants] = useState([]);
  const [uniqueGraphNames, setUniqueGraphNames] = useState([]);
  const [graphsObj, setGraphsObj] = useState(null);
  const [autoCompleteText, setAutoCompleteText] = useState('');
  const [inputValue, setInputValue] = useState({
    tenantId: '',
    tenantKey: '',
    tenantName: '',
    graphName: '',
    graphId: '',
    version: ''
  });
  const [jsonBoxValue, setJsonBoxValue] = useState('');
  const [jsonError, setJsonError] = useState('');

  useEffect(() => {
    const getTenantsConfig = async () => {
      try {
        const { isStackB } = utils.getStackBParams();
        const baseAuth = { Authorization: `Bearer ${utils.getToken()}` };
        const headers = await utils.resolveStackBHeaders(baseAuth);
        const v2Base = isStackB ? env.AI_ADMIN_API_BASE_URL_V2_STACKB : env.AI_ADMIN_API_BASE_URL_V2;
        const response = await axios.get(`${v2Base}${constants.GET_TENANTS_CONFIG_DATA_URL}`, { headers });
        if (response && response.data && response.data.data) {
          const filteredData = _.map(response.data.data, (tenantItem) =>
            _.pick(tenantItem, ['tenantId', 'tenantKey', 'name', 'title'])
          );
          setTenants(filteredData);
        }
      } catch (error) {
        Framework.getStoreRegistry().getStore('notificationState').set({
          open: true,
          message: 'Error fetching tenants',
          type: 'error'
        });
      }
    };

    getTenantsConfig();
  }, []);

  useEffect(() => {
    const fetchGraphs = async () => {
      try {
        const { isStackB } = utils.getStackBParams();
        const base = {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${utils.getToken()}`,
          [constants.BERLIN_TENANT_ID_HEADER]: inputValue.tenantId
        };
        const headers = await utils.resolveStackBHeaders(base);
        const v1Base = isStackB ? env.AI_ADMIN_API_BASE_URL_STACKB : env.AI_ADMIN_API_BASE_URL;
        const response = await axios.get(`${v1Base}/graphs`, { headers });
        if (response.data.success) {
          const allGraphs = response.data.graphs.filter((g) => g.isActive);
          setGraphsObj(allGraphs);
          const uniqueGraphNamesList = Array.from(new Map(allGraphs.map((g) => [g.filename, g])).values()).map((g) => ({
            graphName: g.name,
            filename: g.filename,
            graphId: g.graphId
          }));
          setUniqueGraphNames(uniqueGraphNamesList);
        }
      } catch (err) {
        // error handling
      }
    };
    if (inputValue.tenantId) {
      fetchGraphs();
    }
  }, [inputValue.tenantId]);

  useEffect(() => {
    setInputValue((prev) => ({ ...prev, graphName: '', graphId: '', version: '' }));
  }, [inputValue.tenantId]);

  useEffect(() => {
    const tenantObj = tenants.find((tenantItem) => tenantItem.name === tenant);
    setInputValue({
      ...inputValue,
      tenantId: tenantObj?.tenantId || '',
      tenantKey: tenantObj?.tenantKey || '',
      tenantName: tenantObj?.name || ''
    });
  }, [tenant]);

  const handleInputChange = (event) => {
    const {
      target: { name, value }
    } = event;
    setInputValue((prev) => ({ ...prev, [name]: value }));
  };

  const handleTenant = (event) => {
    setTenant(event.target.value);
  };

  const handleGraphNameChange = (event, selected) => {
    if (selected) {
      const selectedGraph = graphsObj.find((g) => g.filename === selected.filename);
      setInputValue((prev) => ({
        ...prev,
        graphName: selectedGraph.name,
        graphId: selectedGraph.graphId
      }));
      setAutoCompleteText(selected.graphName);
    } else {
      setInputValue((prev) => ({ ...prev, graphName: '', graphId: '', version: '' }));
      setAutoCompleteText('');
    }
  };

  const handleVersionChange = (event) => {
    const versionGraph = event.target.value;
    if (versionGraph) {
      setInputValue((prev) => ({ ...prev, version: `${versionGraph}` }));
    }
  };

  const handleJsonBoxChange = (event) => {
    const val = event.target.value;
    setJsonBoxValue(val);
    if (!val.trim()) {
      setJsonError('');
      return;
    }
    try {
      JSON.parse(val);
      setJsonError('');
    } catch (e) {
      setJsonError('Invalid JSON');
    }
  };

  const handleSave = () => {
    let parsedJson = {};
    if (jsonBoxValue.trim()) {
      try {
        parsedJson = JSON.parse(jsonBoxValue);
      } catch (e) {
        setJsonError('Invalid JSON');
        return;
      }
    }
    const configData = { ...inputValue, extraConfig: parsedJson };
    const stringifiedObject = JSON.stringify(configData);
    const safeString = encodeURIComponent(stringifiedObject);
    const base64String = btoa(safeString);
    let launchPath = `/berlin/guided-study-testbed/${inputValue.tenantName}/launch?configs=${base64String}`;
    launchPath = utils.appendStackBQueryToPathIfNeeded(launchPath);
    history.push(launchPath);
  };

  const isLaunchDisabled =
    !tenant ||
    !inputValue.tenantId ||
    !inputValue.tenantKey ||
    !inputValue.graphId ||
    !inputValue.version ||
    !!jsonError;

  return (
    <Paper elevation={3}>
      <Typography variant="h5" align="center" className={classes.headerTitle} spacing={9}>
        Configure Guided Study Chat App
      </Typography>
      <Box>
        <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
          <Grid container direction="row" className={classes.formSection}>
            <Grid item xs={10} direction="row">
              <Box className={classes.formRowBox}>
                {/* eslint-disable */}
                <FormControl className={classes.formControl} size="small" required>
                  <InputLabel id="tenant">Select Tenant</InputLabel>
                  <Select
                    labelId="tenant-label"
                    label="Select Tenant"
                    name="tenant"
                    onChange={handleTenant}
                    value={tenant}
                  >
                    {tenants.map((t) => (
                      <MenuItem key={t.name} value={t.name}>
                        {t.title}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label="Tenant ID"
                  className={classes.configInput}
                  margin="dense"
                  name="tenantId"
                  onChange={handleInputChange}
                  value={inputValue.tenantId || ''}
                  variant="outlined"
                  required
                />
              </Box>
            </Grid>
          </Grid>

          <Grid container direction="row" className={classes.formSection}>
            <Grid item xs={10} direction="row">
              <Box className={classes.formRowBox}>
                <TextField
                  label="Tenant Key"
                  className={classes.configInput}
                  margin="dense"
                  name="tenantKey"
                  onChange={handleInputChange}
                  value={inputValue.tenantKey || ''}
                  variant="outlined"
                  required
                />
              </Box>
            </Grid>
          </Grid>

          <Grid container direction="row" className={classes.formSection}>
            <Grid item xs={10} direction="row">
              <Box className={classes.formRowBox}>
                <FormControl className={classes.formControl} size="small" required>
                  {uniqueGraphNames.length > 0 ? (
                    <Autocomplete
                      options={uniqueGraphNames}
                      getOptionLabel={(option) => option.graphName}
                      isOptionEqualToValue={(option, value) => option.graphId === value.graphId}
                      inputValue={autoCompleteText}
                      value={uniqueGraphNames.find((option) => option.graphId === inputValue.graphId) || null}
                      onInputChange={(event, value) => setAutoCompleteText(value)}
                      onChange={handleGraphNameChange}
                      onBlur={() => {
                        const matched = uniqueGraphNames.find((g) => g.graphName === autoCompleteText);
                        if (!matched) {
                          setInputValue((prev) => ({ ...prev, graphName: '', graphId: '', version: '' }));
                          setAutoCompleteText('');
                        }
                      }}
                      ListboxProps={{
                        className: classes.autocompleteListbox
                      }}
                      renderOption={(props, option) => (
                        <ListItem {...props} className={classes.graphOption}>
                          <Typography>{option.graphName}</Typography>
                          <Typography variant="caption" color="textSecondary" className={classes.graphId}>
                            {option.graphId}
                          </Typography>
                        </ListItem>
                      )}
                      renderInput={(params) => (
                        <TextField
                          size="small"
                          variant="outlined"
                          required
                          {...params}
                          label="Graph Name"
                          InputProps={{
                            ...params.InputProps,
                            style: { height: 41, padding: 0 },
                            inputProps: { ...params.inputProps, style: { padding: '6px 8px' } }
                          }}
                          InputLabelProps={{ style: { fontSize: '0.9rem', top: '-7px' } }}
                        />
                      )}
                    />
                  ) : (
                    <TextField disabled size="small" variant="outlined" label="No Graph Name Found" />
                  )}
                </FormControl>
                <FormControl className={classes.formControl} size="small" required>
                  {uniqueGraphNames.length > 0 ? (
                    <>
                      <InputLabel id="version-label">Version</InputLabel>
                      <Select
                        labelId="version-label"
                        label="Version"
                        value={inputValue?.version}
                        onChange={handleVersionChange}
                        disabled={!inputValue?.graphName}
                      >
                        {graphsObj
                          .filter((g) => g.filename === `${inputValue.graphName}.json`)
                          .map((g) => (
                            <MenuItem key={g.version} value={g.version}>
                              {`Version ${g.version} (${g.lifeCycleStatus})`}
                            </MenuItem>
                          ))}
                      </Select>
                    </>
                  ) : (
                    <TextField disabled size="small" variant="outlined" label="No Version Found" />
                  )}
                </FormControl>
              </Box>
            </Grid>
          </Grid>

          <Grid container direction="row" className={classes.formSection}>
            <Grid item xs={10} direction="row">
              <Box className={classes.tabPaddingBox} style={{ marginTop: '16px' }}>
                <TextField
                  label="JSON Config"
                  multiline
                  minRows={6}
                  maxRows={12}
                  fullWidth
                  variant="outlined"
                  value={jsonBoxValue}
                  onChange={handleJsonBoxChange}
                  error={!!jsonError}
                  helperText={jsonError || ' '}
                  placeholder="{}"
                  InputProps={{
                    style: { backgroundColor: '#ffffff', fontFamily: 'monospace', fontSize: '0.875rem' }
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </Box>
      <br />
      <div className={classes.flexContainerBottom}>
        <div className={classes.ButtonClass}>
          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              size="small"
              onClick={() => handleSave()}
              className={classes.launchButton}
              disabled={isLaunchDisabled}
            >
              LAUNCH
            </Button>
          </Stack>
        </div>
        <div className={classes.ButtonClass}>
          <div className={classes.versionText}>{`AITutor - v${version}`}</div>
        </div>
      </div>
    </Paper>
  );
};

export default GuidedStudyTestbedConfigure;
